import {
  useContext, useEffect, useRef, useState
} from 'react';
import Axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  Card, Grid, Tabs, Tab, Typography, TextField, Button, FormControl,
  CircularProgress, Autocomplete
} from '@mui/material';
import { CheckCircleOutline as CheckCircleIcon } from '@mui/icons-material';
import { countries as AllCountries } from 'countries-list';

import { useAuth } from 'src/hooks/useAuth';
import ConfigContext from 'src/contexts/ConfigContext';
import { subscriptionTypes } from 'src/config';
import {
  appendContactSupport, axiosHeaders, getLocalisedErrorString, getMaskedCurrencyText, isActionPermitted,
  permissions, validateEmail
} from 'src/utils/helpers';
import SubCompanyAccessModal from 'src/subscription/components/SubCompanyAccessModal/SubCompanyAccessModal';
import LoadingIndicator from 'src/shared/components/LoadingIndicator';
import Page from 'src/shared/screens/Page';
import styles from './style';

function CheckoutNew() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, ready } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { API, BRAND_NAME } = useContext(ConfigContext);

  const auth = useAuth();
  const { user } = auth;

  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [cards, setCards] = useState([]);
  const [defaultCard, setDefaultCard] = useState();
  const [addingCard, setAddingCard] = useState(false);
  const [activatingCard, setActivatingCard] = useState(false);
  const [fetchingCard, setFetchingCard] = useState(true);

  const [selectedSubscription, setSelectedSubscription] = useState(subscriptionTypes.starter);
  const [type, setType] = useState(0);
  const [creditsAmount, setCreditsAmount] = useState(0);
  const [planAmount, setPlanAmount] = useState(0);
  const [billingPlanId, setBillingPlanId] = useState('');
  const [isEnterprise, setIsEnterprise] = useState(false);

  const [email, setEmail] = useState(user.email);
  const [emailErr, setEmailErr] = useState('');

  const [billingDetails, setBillingDetails] = useState({
    address: '',
    postalCode: '',
    city: '',
    country: '',
    vatNumber: '',
    phone: ''
  });
  const [originalBillingDetails, setOriginalBillingDetails] = useState({
    address: '',
    postalCode: '',
    city: '',
    country: '',
    vatNumber: '',
    phone: ''
  });
  const [updatingBillingDetails, setUpdatingBillingDetails] = useState(false);
  const [billingErrors, setBillingErrors] = useState({
    address: '',
    postalCode: '',
    city: '',
    country: '',
    vatNumber: '',
    phone: ''
  });

  const [countriesList, setCountriesList] = useState([]);

  const subscription = {
    name: 'Business',
    invLimit: 200,
    invUsed: 260,
    usersLimit: 10,
    usersUsed: 4,
    totalInvoices: 12000,
    totalPayment: '135050',
    nextBillingAmount: 2999,
    currPrice: 0,
    ecCharged: 0,
    startDate: '2021-10-12T14:48:12-05:00',
    endDate: '2022-04-12T14:48:12-05:00',
    ecPrice: 0,
    level: 1,
    selfServiceUrl: ''
  };

  const intervalID = useRef(null);

  const formatEuroAmount = (amount) => {
    if (typeof amount !== 'number') return '0,00';
    return amount.toFixed(2).replace('.', ',');
  };

  const getAllCards = async (id) => {
    setFetchingCard(true);
    try {
      const response = await Axios.get(
        API.userCards,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        let defCard = null;
        for (let i = 0; i < response.data.data.length; i++) {
          if (response.data.data[i].id === id) {
            defCard = response.data.data[i];
          }
        }

        if (defCard) {
          setCards([defCard]);
        }
      }
      setFetchingCard(false);
      setActivatingCard(false);
      setAddingCard(false);
    } catch (error) {
      setFetchingCard(false);
      setActivatingCard(false);
      setAddingCard(false);
    }
  };

  const getAllCardsInterval = async () => {
    try {
      const response = await Axios.get(
        API.userCards,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        return response.data.data.length;
      }
      return 0;
    } catch (error) {
      return 0;
    }
  };

  const paymentProfileMethod = async () => {
    try {
      const response = await Axios.get(
        API.subscriptionDetails,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        getAllCards(response.data.data[0].creditCard.id);
        setDefaultCard(response.data.data[0].creditCard.id);
      } else {
        getAllCards(null);
      }
    } catch (error) {
      setActivatingCard(false);
      setAddingCard(false);
    }
  };

  const getBillingDetails = async () => {
    try {
      const response = await Axios.get(
        `${API.getBillingDetails}/${user.companyID}`,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success && response.data.data) {
        setBillingDetails(response.data.data);
        setOriginalBillingDetails(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching billing details:', error);
    }
  };

  const updateBillingDetails = async (billingData) => {
    try {
      const response = await Axios.put(
        `${API.updateBillingDetails}/${user.companyID}`,
        billingData,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        enqueueSnackbar(
          t('BILLING_DETAILS_UPDATE_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 3000
          }
        );
        return true;
      }
      return false;
    } catch (error) {
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, getLocalisedErrorString(error?.response?.data?.i18n || 'BILLING_DETAILS_UPDATE_FAILED', t), t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
      return false;
    }
  };

  useEffect(() => {
    const allCountries = Object.keys(AllCountries).map((key) => ({
      isoCode: key,
      name: AllCountries[key]?.name,
    }));
    setCountriesList(allCountries);

    if (!isActionPermitted(permissions.subscriptionsUpgrade, user.permissions)
      || !isActionPermitted(permissions.subscriptionsDowngrade, user.permissions)
      || !isActionPermitted(permissions.subscriptionsBuyExtra, user.permissions)) {
      enqueueSnackbar(
        t('SUBSCRIPTION_NO_PERMISSION'),
        {
          variant: 'error',
        }
      );

      navigate('/subscription-plan');
    }

    const search = location.search.substring(1);
    const paramsAr = search.split('&');

    for (let i = 0; i < paramsAr.length; i++) {
      const paramParts = paramsAr[i].split('=');
      if (paramParts.length >= 2 && paramParts[0] === 'type') {
        const typeValue = parseInt(paramParts[1], 10);
        setType(typeValue);

        if (typeValue === 0) {
          setSelectedSubscription(
            location.state && location.state.selectedSubscription ? location.state.selectedSubscription : subscriptionTypes.starter
          );
        }
      }

      if (paramParts.length >= 2 && paramParts[0] === 'credits') {
        setCreditsAmount(parseInt(paramParts[1], 10) || 0);
      }

      if (paramParts.length >= 2 && paramParts[0] === 'planAmount') {
        setPlanAmount(parseFloat(paramParts[1]) || 0);
      }

      if (paramParts.length >= 2 && paramParts[0] === 'billingPlanId') {
        setBillingPlanId(paramParts[1] || '');
      }

      if (paramParts.length >= 2 && paramParts[0] === 'isEnterprise') {
        setIsEnterprise(paramParts[1] === 'true');
      }
    }
    paymentProfileMethod();
    getBillingDetails();
  }, []);

  const handleBuyCredits = async () => {
    setLoading(true);
    try {
      const unitPriceInCents = Math.round(planAmount * 100);

      const actualTotal = creditsAmount * planAmount;
      const amountPaidInCents = Math.round(actualTotal * 100);

      const requestBody = {
        billingPlanId: parseInt(billingPlanId) || 11,
        quantity: creditsAmount,
        unitPrice: unitPriceInCents,
        amountPaid: amountPaidInCents,
        isEnterprisePlan: isEnterprise
      };

      const response = await Axios.post(
        API.buyCredits,
        requestBody,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        enqueueSnackbar(
          t('SUBSCRIPTION_BUY_CREDITS_SUCCESS', { amount: creditsAmount }),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
        setTimeout(() => {
          navigate('/subscription-plan');
        }, 2000);
      }
    } catch (err) {
      setLoading(false);
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, getLocalisedErrorString(err?.response?.data?.i18n || 'SUBSCRIPTION_BUY_CREDITS_FAILED', t), t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const handleBuyExtraInvoices = async () => {
    setLoading(true);
    try {
      const response = await Axios.get(API.buyExtraInvoicesPackage, axiosHeaders(localStorage.getItem('PROCYS_accessToken')));
      if (response.data.success) {
        enqueueSnackbar(
          t('SUBSCRIPTION_BUY_EXTRA_PACKAGE_SUCCESS', { ecLimit: subscription.ecLimit || 25 }),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
        setTimeout(() => {
          navigate('/subscription-plan');
          // setLoading(false);
        }, 2000);
      }
    } catch (err) {
      setLoading(false);
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, getLocalisedErrorString(err?.response?.data?.i18n || 'SUBSCRIPTION_BUY_EXTRA_PACKAGE_FAILED', t), t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const handleUpgradeSubscription = async (to) => {
    try {
      const body = {
        action: 'upgrade',
        toSubscription: to
      };

      const response = await Axios.put(
        API.updateSubscription,
        body,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        enqueueSnackbar(
          t('SUBSCRIPTION_UPGRADE_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
        setTimeout(() => {
          if (subscription.totalPayment === 0) {
            navigate('/subscription-plan?feedback-first-payment=success');
          } else {
            navigate('/subscription-plan');
          }
          // setLoading(false);
        }, 2000);
      }
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, getLocalisedErrorString(error?.response?.data?.i18n || 'SUBSCRIPTION_UPGRADE_FAILURE', t), t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const handleDowngradeSubscription = async (to) => {
    try {
      const body = {
        action: 'downgrade',
        toSubscription: to,
        immediately: false
      };

      const response = await Axios.put(
        API.updateSubscription,
        body,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        enqueueSnackbar(
          t('SUBSCRIPTION_DOWNGRADE_SUCCESS'),
          {
            variant: 'success',
            autoHideDuration: 5000
          }
        );
        setTimeout(() => {
          navigate('/subscription-plan');
          // setLoading(false);
        }, 2000);
      }
    } catch (err) {
      setLoading(false);
      enqueueSnackbar(
        appendContactSupport(window.config.support_email, getLocalisedErrorString(err?.response?.data?.i18n || 'SUBSCRIPTION_DOWNGRADE_FAILURE', t), t),
        {
          variant: 'error',
          autoHideDuration: 5000
        }
      );
    }
  };

  const onSubscribe = (to) => {
    setLoading(true);
    if (selectedSubscription.level > subscription.level) {
      handleUpgradeSubscription(to);
      return;
    }

    handleDowngradeSubscription(to);
  };

  const activateCard = async (cardID) => {
    setActivatingCard(true);
    try {
      const response = await Axios.post(
        API.changeDefaultPaymentMethod,
        { paymentProfileID: cardID.toString() },
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        paymentProfileMethod();
        enqueueSnackbar(t('PROCYS_CHANGE_DEFAULT_PAYMENT_METHOD_SUCCESS'), {
          variant: 'success',
        });
      } else {
        setActivatingCard(false);
      }
    } catch (error) {
      enqueueSnackbar(appendContactSupport(window.config.support_email, t('PROCYS_CHANGE_DEFAULT_PAYMENT_METHOD_FAILURE'), t), {
        variant: 'error',
      });
      setActivatingCard(false);
    }
  };

  const handleChangeField = (value, prop, setter, errSetter) => {
    if (!value) {
      errSetter(t('INTEGRATIONS_PROP_CANNOT_BE_EMPTY', { prop }));
    } else {
      errSetter('');
    }

    setter(value);
  };

  const handleBillingFieldChange = (value, field) => {
    setBillingDetails(prev => ({
      ...prev,
      [field]: value
    }));

    if (billingErrors[field]) {
      setBillingErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateBillingDetails = () => {
    const errors = {};
    let isValid = true;

    const requiredFields = ['address', 'postalCode', 'city', 'country', 'phone'];

    requiredFields.forEach(field => {
      if (!billingDetails[field] || billingDetails[field].trim() === '') {
        errors[field] = t('FIELD_REQUIRED');
        isValid = false;
      }
    });

    const emailResponse = validateEmail(email);
    if (!emailResponse.isValid) {
      setEmailErr(emailResponse.errorMessage);
      isValid = false;
    } else {
      setEmailErr('');
    }

    setBillingErrors(errors);
    return isValid;
  };

  const isBillingDetailsValid = () => {
    const requiredFields = ['address', 'postalCode', 'city', 'country', 'phone'];

    const allFieldsFilled = requiredFields.every(field =>
      billingDetails[field] && billingDetails[field].trim() !== ''
    );

    const emailValid = email && email.trim() !== '' && validateEmail(email).isValid;

    return allFieldsFilled && emailValid;
  };

  const handleGotoPayment = async () => {
    if (!validateBillingDetails()) {
      return;
    }

    if (hasBillingDetailsChanged()) {
      setUpdatingBillingDetails(true);
      const success = await updateBillingDetails(billingDetails);
      setUpdatingBillingDetails(false);

      if (success) {
        setOriginalBillingDetails(billingDetails);
        setTabIndex(1);
      }
    } else {
      setTabIndex(1);
    }
  };

  const handleGotoSummary = () => {
    setTabIndex(2);
  };

  const onStartInterval = () => {
    intervalID.current = setInterval(async () => {
      const cardCount = await getAllCardsInterval();
      if (cardCount > cards.length) {
        paymentProfileMethod();
        clearInterval(intervalID.current);
      }
    }, 5000);
  };

  const onNavigateToAddCard = () => {
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;

    const width = screenWidth > 720 ? 720 : screenWidth - 20;
    const height = screenHeight > 700 ? 700 : screenHeight - 20;

    const excessH = screenHeight - height - 100;
    const top = excessH > 0 ? excessH / 2 : 10;

    const excessW = screenWidth - width;
    const left = excessW > 0 ? excessW / 2 : 10;

    window.open(subscription.selfServiceUrl, 'ProcysBillingInformation', `height=${height},width=${width},top=${top},left=${left}`);
    onStartInterval();
  };

  const renderCheckoutBtn = () => (
    loading ? (
      <Grid style={styles.action}>
        <CircularProgress sx={{ color: '#3E8AFF' }} size={20} />
      </Grid>
    ) : (
      <Button
        variant="contained"
        style={styles.btn}
        onClick={() => {
          if (type === 0) {
            onSubscribe(selectedSubscription.label.toLowerCase());
          } else if (type === 1) {
            handleBuyExtraInvoices();
          } else if (type === 2) {
            handleBuyCredits();
          }
        }}
      >
        {ready && t('PAY_NOW')}
      </Button>
    )
  );

  const renderFooter = (prevIndex, nextAction, disableNext = false) => (
    <Grid style={styles.footerContainer}>
      {
        prevIndex >= 0 ? (
          <Button disabled={loading || addingCard || activatingCard || updatingBillingDetails} style={styles.prevBtn} onClick={() => setTabIndex(prevIndex)}>
            {ready && t('SUBSCRIPTION_PREVIOUS')}
          </Button>
        ) : (
          <Button style={styles.prevBtn} onClick={() => navigate('/subscription-plan')}>
            {ready && t('SUBSCRIPTION_BACK_TO_SUBSCRIPTIONS')}
          </Button>
        )
      }
      {
        nextAction
          ? (
            <Button
              disabled={loading || addingCard || activatingCard || disableNext || updatingBillingDetails}
              variant="contained"
              style={styles.btn}
              onClick={nextAction}
            >
              {updatingBillingDetails ? (
                <>
                  <CircularProgress sx={{ color: '#fff', marginRight: 1 }} size={16} />
                  {ready && t('UPDATING_BILLING_DETAILS')}
                </>
              ) : (
                ready && t('SUBSCRIPTION_NEXT')
              )}
            </Button>
          ) : (
            renderCheckoutBtn()
          )
      }
    </Grid>
  );

  const userDetails = () => (
    <Grid>
      <Typography style={styles.tabTitle}>
        {ready && t('CHECKOUT_BILLING_DETAILS')}
      </Typography>
      <Grid style={styles.fieldsContainer}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={ready && t('SUBSCRIPTION_EMAIL')}
              name="email"
              onChange={(e) => handleChangeField(e.target.value.toLowerCase(), t('SUBSCRIPTION_EMAIL'), setEmail, setEmailErr)}
              type="email"
              value={email}
              variant="outlined"
              required
              error={Boolean(emailErr)}
              helperText={emailErr}
              style={styles.fieldStyle}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={ready && t('BILLING_ADDRESS')}
              name="address"
              onChange={(e) => handleBillingFieldChange(e.target.value, 'address')}
              type="text"
              value={billingDetails.address}
              variant="outlined"
              required
              error={Boolean(billingErrors.address)}
              helperText={billingErrors.address}
              style={styles.fieldStyle}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={ready && t('BILLING_CITY')}
              name="city"
              onChange={(e) => handleBillingFieldChange(e.target.value, 'city')}
              type="text"
              value={billingDetails.city}
              variant="outlined"
              required
              error={Boolean(billingErrors.city)}
              helperText={billingErrors.city}
              style={styles.fieldStyle}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={ready && t('BILLING_POSTAL_CODE')}
              name="postalCode"
              onChange={(e) => handleBillingFieldChange(e.target.value, 'postalCode')}
              type="text"
              value={billingDetails.postalCode}
              variant="outlined"
              required
              error={Boolean(billingErrors.postalCode)}
              helperText={billingErrors.postalCode}
              style={styles.fieldStyle}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl
              fullWidth
              variant="outlined"
              error={Boolean(billingErrors.country)}
              style={styles.fieldStyle}
            >
              <Autocomplete
                value={billingDetails.country ?
                  countriesList.find((o) => o.isoCode === billingDetails.country || o.name === billingDetails.country) : null}
                onChange={(e, v) => handleBillingFieldChange(v ? v.isoCode : '', 'country')}
                options={countriesList}
                getOptionLabel={(option) => option.name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={ready && t('BILLING_COUNTRY')}
                    variant="outlined"
                    required
                    error={Boolean(billingErrors.country)}
                    helperText={billingErrors.country}
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={ready && t('BILLING_VAT_NUMBER')}
              name="vatNumber"
              onChange={(e) => handleBillingFieldChange(e.target.value, 'vatNumber')}
              type="text"
              value={billingDetails.vatNumber}
              variant="outlined"
              error={Boolean(billingErrors.vatNumber)}
              helperText={billingErrors.vatNumber}
              style={styles.fieldStyle}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={ready && t('BILLING_PHONE')}
              name="phone"
              onChange={(e) => handleBillingFieldChange(e.target.value, 'phone')}
              type="tel"
              value={billingDetails.phone}
              variant="outlined"
              required
              error={Boolean(billingErrors.phone)}
              helperText={billingErrors.phone}
              style={styles.fieldStyle}
            />
          </Grid>
        </Grid>
      </Grid>
      {renderFooter(-1, handleGotoPayment, !isBillingDetailsValid() || updatingBillingDetails)}
    </Grid>
  );

  const paymentDetails = () => (
    <Grid>
      <Typography style={styles.tabTitle}>
        {ready && t('SUBSCRIPTION_PAYMENT_METHOD')}
      </Typography>
      <Grid style={styles.paymentTabContainer}>
        {
          addingCard || activatingCard || fetchingCard
            ? (<LoadingIndicator
              text={fetchingCard
                ? ready && t('SUBSCRIPTION_LOADING_CARD')
                : activatingCard ? ready && t('SUBSCRIPTION_ACTIVATING_CARD')
                  : ready && t('SUBSCRIPTION_ADDING_CARD')} />)
            : (
              <>
                <Grid container spacing={3}>
                  {
                    cards && cards.length > 0 && cards.map((c) => (
                      <Grid item md={4} sm={6} xs={12} key={c.id}>
                        <Grid style={styles.card}>
                          <Typography style={styles.cardNumber}>{c.maskedCardNumber}</Typography>
                          <Grid style={styles.cardFooter}>
                            <Typography style={styles.cardFooterText}>{`${c.firstName || ''} ${c.lastName || ''}`}</Typography>
                            <Typography style={styles.cardFooterText}>
                              {
                                ready && `${c.expirationMonth.toString().length !== 2
                                  ? `0${c.expirationMonth}`
                                  : c.expirationMonth}/${c.expirationYear}`
                              }
                            </Typography>
                            {c.cardType === 'master' && <img src="/static/images/master.svg" alt="" style={styles.cardType} />}
                            {c.cardType === 'visa' && <img src="/static/images/visa.svg" alt="" style={styles.cardType} />}
                            {c.cardType === 'american_express' && <img src="/static/images/amex.svg" alt="" style={styles.cardType} />}
                          </Grid>
                        </Grid>
                        <Grid style={styles.cardActionsContainer}>
                          {
                            <Grid style={styles.makeActiveContainer}>
                              <Button
                                variant="outlined"
                                style={styles.changePlan}
                                onClick={onNavigateToAddCard}
                              >
                                {ready && t('SUBSCRIPTION_CHANGE_CARD')}
                              </Button>
                            </Grid>
                          }
                          {
                            defaultCard === c.id
                              ? (
                                <Grid style={styles.activeContainer}>
                                  <CheckCircleIcon style={styles.checkIcon} />
                                  <Typography style={styles.activeText}>Active</Typography>
                                </Grid>
                              )
                              : (
                                <Grid style={styles.makeActiveContainer}>
                                  <Button variant="outlined" style={styles.makeActiveBtn} onClick={() => activateCard(c.id)}>
                                    {ready && t('SUBSCRIPTION_AVTIVATE_CARD')}
                                  </Button>
                                </Grid>
                              )
                          }
                        </Grid>
                      </Grid>
                    ))
                  }
                  {
                    (!cards || cards.length === 0) && (
                      <Grid style={styles.addCardContainer}>
                        <Typography style={styles.normalText}>{ready && t('SUBSCRIPTION_NO_CARD')}</Typography>
                        <Button variant="outlined" style={styles.addCardBtn} onClick={onNavigateToAddCard}>{ready && t('SUBSCRIPTION_ADD_CARD')}</Button>
                      </Grid>
                    )
                  }
                </Grid>
              </>
            )
        }
      </Grid>
      {renderFooter(0, handleGotoSummary, false)}
    </Grid>
  );

  const summaryDetails = () => (
    <Grid container justifyContent="center" style={{ width: '100%' }}>
      <Grid item xs={12} md={8} lg={6}>
        <Typography style={styles.tabTitle}>
          {ready && t('CHECKOUT_CONFIRM')}
        </Typography>

        <Grid container spacing={3} style={{ width: '100%', margin: 0, padding: 0 }}>
          <Grid item xs={12} style={{ paddingLeft: 0 }}>
            <Typography variant="subtitle1" style={{ marginBottom: 8, fontWeight: 'bold', fontSize: '18px', textAlign: 'left' }}>
              {ready && t('CHECKOUT_BILLING_DETAILS')}
            </Typography>
            <Grid container style={styles.summaryContainer}>
              <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                {ready && t('SUBSCRIPTION_EMAIL')}:
              </Grid>
              <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                {email}
              </Grid>
              <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                {ready && t('BILLING_ADDRESS')}:
              </Grid>
              <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                {billingDetails.address}
              </Grid>
              <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                {ready && t('BILLING_CITY')}:
              </Grid>
              <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                {billingDetails.city}
              </Grid>
              <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                {ready && t('BILLING_POSTAL_CODE')}:
              </Grid>
              <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                {billingDetails.postalCode}
              </Grid>
              <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                {ready && t('BILLING_COUNTRY')}:
              </Grid>
              <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                {billingDetails.country ?
                  (countriesList.find((o) => o.isoCode === billingDetails.country || o.name === billingDetails.country)?.name || billingDetails.country)
                  : ''
                }
              </Grid>
              {billingDetails.vatNumber && (
                <>
                  <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                    {ready && t('BILLING_VAT_NUMBER')}:
                  </Grid>
                  <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                    {billingDetails.vatNumber}
                  </Grid>
                </>
              )}
              {billingDetails.phone && (
                <>
                  <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                    {ready && t('BILLING_PHONE')}:
                  </Grid>
                  <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                    {billingDetails.phone}
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>

        {cards && cards.length > 0 && (
          <Grid container spacing={3} style={{ width: '100%', margin: 0, padding: 0, marginTop: 8 }}>
            <Grid item xs={12} style={{ paddingLeft: 0 }}>
              <Typography variant="subtitle1" style={{ marginBottom: 8, fontWeight: 'bold', fontSize: '18px', textAlign: 'left' }}>
                {ready && t('SUBSCRIPTION_PAYMENT_METHOD')}
              </Typography>
              <Grid container style={styles.summaryContainer}>
                {cards.map((c) => (
                  defaultCard === c.id && (
                    <Grid container key={c.id}>
                      <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                        {ready && t('BILLING_PAYMENT_SUBSCRIPTION_CARD_NUMBER')}:
                      </Grid>
                      <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                        {c.maskedCardNumber}
                      </Grid>
                      <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                        {ready && t('BILLING_PAYMENT_SUBSCRIPTION_CARDHOLDER')}:
                      </Grid>
                      <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                        {`${c.firstName || ''} ${c.lastName || ''}`}
                      </Grid>
                      <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                        {ready && t('BILLING_PAYMENT_CARD_EXPIRY')}:
                      </Grid>
                      <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                        {`${c.expirationMonth.toString().length !== 2 ? `0${c.expirationMonth}` : c.expirationMonth}/${c.expirationYear}`}
                      </Grid>
                    </Grid>
                  )
                ))}
              </Grid>
            </Grid>
          </Grid>
        )}

        <Grid container spacing={3} style={{ width: '100%', margin: 0, padding: 0, marginTop: 8 }}>
          <Grid item xs={12} style={{ paddingLeft: 0 }}>
            <Typography variant="subtitle1" style={{ marginBottom: 8, fontWeight: 'bold', fontSize: '18px', textAlign: 'left' }}>
              {ready && t('BILLING_PAYMENT_PAYMENT_INFO')}
            </Typography>
            <Grid container style={styles.summaryContainer}>
              {type === 2 && (
                <>
                  <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                    {ready && t('NUMBER_OF_CREDITS')}:
                  </Grid>
                  <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                    {creditsAmount}
                  </Grid>
                  <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                    {ready && t('PRICE_PER_CREDIT')}:
                  </Grid>
                  <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                    {planAmount > 0 ? formatEuroAmount(planAmount) : '€1,00'}
                  </Grid>
                </>
              )}
              {type === 1 && (
                <>
                  <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                    {ready && t('SUBSCRIPTION_EX_INV_FEE')}:
                  </Grid>
                  <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                    €{getMaskedCurrencyText(subscription.ecPrice.toString())}
                  </Grid>
                </>
              )}
              {type === 0 && (
                <>
                  <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                    {ready && t('SUBSCRIPTION_SUBSCRIPTION_FEE', { subscription: t(selectedSubscription.i18n) })}:
                  </Grid>
                  <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                    €{getMaskedCurrencyText((selectedSubscription.price ? selectedSubscription.price.toString() : '0').toString())}
                  </Grid>
                </>
              )}
              <Grid item sm={4} xs={12} style={styles.summaryLabel}>
                {ready && t('SUBSCRIPTION_TAX')}:
              </Grid>
              <Grid item sm={8} xs={12} style={styles.summaryAmount}>
                -
              </Grid>
              <Grid item xs={12} style={styles.hrLine} />
              <Grid item sm={4} xs={12} style={styles.totalLabel}>
                {ready && t('SUBSCRIPTION_TOTAL')}:
              </Grid>
              <Grid item sm={8} xs={12} style={styles.totalAmount}>
                {type === 2 ? (
                  `€${(creditsAmount * planAmount).toFixed(2).replace('.', ',')}`
                ) : type === 1 ? (
                  `€${getMaskedCurrencyText(subscription.ecPrice.toString())}`
                ) : type === 0 ? (
                  `€${getMaskedCurrencyText((selectedSubscription.price ? selectedSubscription.price : 0).toString())}`
                ) : (
                  '€0,00'
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid style={styles.footerContainer}>
          <Button disabled={loading || addingCard || activatingCard || updatingBillingDetails} style={styles.prevBtn} onClick={() => setTabIndex(1)}>
            {ready && t('SUBSCRIPTION_PREVIOUS')}
          </Button>
          {renderCheckoutBtn()}
        </Grid>
      </Grid>
    </Grid>
  );

  const hasBillingDetailsChanged = () => {
    if (email !== user.email) {
      return true;
    }

    const fieldsToCheck = ['address', 'postalCode', 'city', 'country', 'vatNumber', 'phone'];

    return fieldsToCheck.some(field =>
      billingDetails[field] !== originalBillingDetails[field]
    );
  };

  if ((user.isSubCompany || user.isPartnerSubCompany) && user.parent) {
    return <SubCompanyAccessModal openModal parentName={user.parentName} />;
  }

  return (
    <Page
      style={styles.root}
      title={ready && `${t('CHECKOUT_PAGE_TITLE')} | ${BRAND_NAME}`}
    >
      <Grid container spacing={3} style={styles.wrapper}>
        <Grid item xs={12} style={styles.container}>
          <Card style={styles.planContainer}>
            <Tabs
              value={tabIndex}
              sx={{
                '& .MuiTabs-flexContainer': styles.tabsWrapper,
              }}
            >
              <Tab
                sx={styles.tabContainer}
                icon={<Grid style={tabIndex === 0 ? styles.selectedCircle : styles.circle} />}
                label={ready && t('CHECKOUT_DETAILS')}
              />
              <Tab
                sx={styles.tabContainer}
                icon={<Grid style={tabIndex === 1 ? styles.selectedCircle : styles.circle} />}
                label={ready && t('CHECKOUT_PAYMENT')}
              />
              <Tab
                sx={styles.tabContainer}
                icon={<Grid style={tabIndex === 2 ? styles.selectedCircle : styles.circle} />}
                label={ready && t('CHECKOUT_CONFIRM')}
              />
            </Tabs>
            <Grid style={styles.bodyContainer}>
              {
                tabIndex === 0 && userDetails()
              }
              {
                tabIndex === 1 && paymentDetails()
              }
              {
                tabIndex === 2 && summaryDetails()
              }
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Page>
  );
}

export default CheckoutNew;
